import { useMemo, useState } from 'react';

import { useParams } from 'react-router';

import { Grid } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import { DownloadModeloArquivoBtn } from 'components/buttons/DownloadModeloArquivoBtn';
import { BbarForm } from 'components/docked/BbarForm';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useLoaderContext } from 'context/LoaderContext';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import {
    ConfigPedidoGeracaoConfigs,
    ConfigPedidoHeaderActions,
    ConfigPedidoImportacaoUploadBtn,
    ConfigPedidoLojasMetasGrid,
    ConfigPedidoLojasModal,
} from 'pages/compra/configuracao-pedido/components';
import { ConfigPedidoListProvider } from 'pages/compra/configuracao-pedido/contexts';
import { useConfigPedidoDirty, useMutatePedidosSazonais, useQueryPedidosSazonais } from 'pages/compra/configuracao-pedido/hooks';
import { ConfigPedidoPedidosSazonais, LojaMetaAtingidaProps, LojasProps } from 'pages/compra/configuracao-pedido/types';

const initialValues: ConfigPedidoPedidosSazonais = {
    lojasMetaAtingida: [],
    percentualNotaRoyaltiesSazonal: null,
};

export function ConfigPedidoPedidosSazonaisForm() {
    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { data, isFetching } = useQueryPedidosSazonais();
    const { onSubmit, isLoading } = useMutatePedidosSazonais();
    const { focusError } = useFocusErrorFormik();

    const validationSchema = useMemo(
        () =>
            Yup.object({
                percentualNotaRoyaltiesSazonal: Yup.object().required('Tipo de percentual de nota fiscal é obrigatório'),
            }),
        [],
    );

    const [openLojasModal, setOpenLojasModal] = useState(false);

    useLoaderContext(isFetching || isLoading);

    const formik = useFormik({
        initialValues: data || initialValues,
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit,
        enableReinitialize: true,
    });

    const { values, setFieldValue, submitForm, errors, dirty } = formik;

    focusError(errors);

    useConfigPedidoDirty(dirty);

    /**
     * Adiciona lojas selecionadas ao formulário
     */
    const addLojas = (lojas: LojasProps[]) => {
        const newLojas: LojaMetaAtingidaProps[] = lojas
            .filter((loja) => !values.lojasMetaAtingida.some((m) => m.loja.idLoja === loja.idLoja))
            .map((loja) => ({
                loja,
                fgMetaAtingida: false,
                tipoLogisticaLoja: null,
                configPedido: { idConfigPedido: Number(id) },
            }));

        setFieldValue('lojasMetaAtingida', [...values.lojasMetaAtingida, ...newLojas]);
    };

    /**
     * Remove lojas selecionadas do formulário
     */
    const removeLojas = (idList: Set<GridRowId>) => {
        setFieldValue(
            'lojasMetaAtingida',
            values.lojasMetaAtingida.filter((m) => !idList.has(m.loja.idLoja)),
        );
    };

    /**
     * Callback de sucesso do upload do arquivo
     */
    const onSuccessUploadFile = ({ message, lojasMetaAtingida }: { message: string; lojasMetaAtingida: LojaMetaAtingidaProps[] }) => {
        if (message) enqueueSnackbar(message, { variant: 'warning' });

        setFieldValue('lojasMetaAtingida', lojasMetaAtingida);
    };

    return (
        <FormikProvider value={formik}>
            <ConfigPedidoLojasModal
                open={openLojasModal}
                setOpen={setOpenLojasModal}
                handleAdicionarLojas={addLojas}
                requestOptionsLojas={{
                    url: '/gestao/loja/findbyidsconfigpedido',
                    sort: [{ property: 'idLoja', direction: 'ASC' }],
                    filter: [{ property: 'idConfigPedido', operator: 'eq', value: id }],
                }}
            />

            <Block>
                <SubTitle label="Importar metas" />

                <Grid container sx={{ justifyContent: 'space-between', gap: 2, width: '100%' }}>
                    <Grid>
                        <ConfigPedidoImportacaoUploadBtn
                            url={`/gestao/lojametaatingida/configpedido/${id}/upload`}
                            onSuccessUploadFile={(data) => onSuccessUploadFile(data.data)}
                        />
                    </Grid>

                    <Grid>
                        <DownloadModeloArquivoBtn dsChave="URL_ARQUIVO_MODELO_LOJA_META_ATINGIDA" />
                    </Grid>
                </Grid>
            </Block>

            <ConfigPedidoListProvider onAdd={() => setOpenLojasModal(true)} onRemove={removeLojas} showSearchField={false} showFilters={true}>
                <Block>
                    <SubTitle label="Lojas" />

                    <ConfigPedidoHeaderActions />

                    <ConfigPedidoLojasMetasGrid />
                </Block>
            </ConfigPedidoListProvider>

            <ConfigPedidoGeracaoConfigs values={values} initialValues={formik.initialValues} setFieldValue={setFieldValue} />

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}
